/**
 * Poker Practice Mode - Quiz for hand strength and betting decisions
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Trophy, Target, Brain, Zap, Check, X, RefreshCw, Star, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { SUITS, BOOSTY_LOGO_URL } from './pokerConstants.js';
import { createDeck, shuffleDeck, bestFiveCardHand } from './pokerEngine.js';

// Quiz Types
const QUIZ_TYPES = {
  HAND_RANKING: 'hand_ranking',
  STARTING_HAND: 'starting_hand',
  POT_ODDS: 'pot_odds',
  OUTS_COUNTING: 'outs_counting'
};

// Hand strength names in order
const HAND_NAMES = [
  'High Card', 'One Pair', 'Two Pair', 'Three of a Kind', 
  'Straight', 'Flush', 'Full House', 'Four of a Kind', 
  'Straight Flush', 'Royal Flush'
];

// Starting hand categories
const STARTING_HANDS = {
  premium: [
    { cards: ['A', 'A'], suited: false, action: 'raise', reason: 'Pocket Aces - the best starting hand' },
    { cards: ['K', 'K'], suited: false, action: 'raise', reason: 'Pocket Kings - second best starting hand' },
    { cards: ['Q', 'Q'], suited: false, action: 'raise', reason: 'Pocket Queens - premium pair' },
    { cards: ['A', 'K'], suited: true, action: 'raise', reason: 'Big Slick suited - premium drawing hand' },
  ],
  strong: [
    { cards: ['J', 'J'], suited: false, action: 'raise', reason: 'Pocket Jacks - strong but vulnerable' },
    { cards: ['A', 'Q'], suited: true, action: 'raise', reason: 'Strong suited ace' },
    { cards: ['A', 'K'], suited: false, action: 'raise', reason: 'Big Slick - strong but can miss flops' },
    { cards: ['10', '10'], suited: false, action: 'raise', reason: 'Middle pair - play aggressively' },
  ],
  playable: [
    { cards: ['9', '9'], suited: false, action: 'call', reason: 'Small pair - set mining potential' },
    { cards: ['A', 'J'], suited: true, action: 'call', reason: 'Suited ace - be cautious with kicker' },
    { cards: ['K', 'Q'], suited: true, action: 'call', reason: 'Suited broadway - good potential' },
    { cards: ['7', '8'], suited: true, action: 'call', reason: 'Suited connectors - implied odds' },
  ],
  fold: [
    { cards: ['7', '2'], suited: false, action: 'fold', reason: 'The worst hand in poker - no potential' },
    { cards: ['9', '3'], suited: false, action: 'fold', reason: 'No connection, no potential' },
    { cards: ['K', '4'], suited: false, action: 'fold', reason: 'Dominated kicker - often second best' },
    { cards: ['J', '3'], suited: false, action: 'fold', reason: 'Weak hand with no straight potential' },
  ]
};

// Generate a random card
const randomCard = (excludeCards = []) => {
  const suits = ['spades', 'hearts', 'diamonds', 'clubs'];
  const ranks = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
  const displays = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
  
  let card;
  let attempts = 0;
  do {
    const suit = suits[Math.floor(Math.random() * suits.length)];
    const rankIdx = Math.floor(Math.random() * ranks.length);
    card = {
      id: `${suit}_${ranks[rankIdx]}`,
      suit,
      rank: ranks[rankIdx],
      display: displays[rankIdx]
    };
    attempts++;
  } while (excludeCards.some(c => c.id === card.id) && attempts < 100);
  
  return card;
};

// Generate random hole cards
const generateHoleCards = (excludeCards = []) => {
  const card1 = randomCard(excludeCards);
  const card2 = randomCard([...excludeCards, card1]);
  return [card1, card2];
};

// Generate a hand with specific strength
const generateHandWithStrength = (minStrength, maxStrength) => {
  let attempts = 0;
  while (attempts < 1000) {
    const holeCards = generateHoleCards();
    const community = [];
    for (let i = 0; i < 5; i++) {
      community.push(randomCard([...holeCards, ...community]));
    }
    
    const hand = bestFiveCardHand([...holeCards, ...community]);
    if (hand && hand.hand) {
      const strength = HAND_NAMES.indexOf(hand.hand.name);
      if (strength >= minStrength && strength <= maxStrength) {
        return { holeCards, community, hand, strength };
      }
    }
    attempts++;
  }
  return null;
};

// Small Card Component
const MiniCard = ({ card, showBack = false }) => {
  if (showBack || !card) {
    return (
      <div className="w-10 h-14 rounded-md bg-gradient-to-br from-purple-600 to-purple-900 border border-purple-400 flex items-center justify-center">
        <img src={BOOSTY_LOGO_URL} alt="" className="h-6 w-auto opacity-80" />
      </div>
    );
  }
  
  const suitInfo = SUITS[card.suit];
  return (
    <div className="w-10 h-14 rounded-md bg-white border border-slate-300 flex flex-col items-center justify-center">
      <span className="text-sm font-bold" style={{ color: suitInfo?.color }}>{card.display}</span>
      <span className="text-xs" style={{ color: suitInfo?.color }}>{suitInfo?.symbol}</span>
    </div>
  );
};

// Quiz Question Component
const QuizQuestion = ({ type, question, options, onAnswer, answered, correct, explanation }) => {
  return (
    <div className="bg-slate-900/90 rounded-xl border border-slate-700 p-6">
      <h3 className="text-lg font-bold text-white mb-4">{question}</h3>
      
      <div className="space-y-2">
        {options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => !answered && onAnswer(option.value)}
            disabled={answered}
            className={`w-full p-3 rounded-lg text-left transition-all ${
              answered
                ? option.correct
                  ? 'bg-green-500/30 border-2 border-green-500 text-green-300'
                  : option.selected
                    ? 'bg-red-500/30 border-2 border-red-500 text-red-300'
                    : 'bg-slate-800/50 border border-slate-600 text-slate-400'
                : 'bg-slate-800/50 border border-slate-600 text-white hover:border-amber-500/50 hover:bg-slate-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <span>{option.label}</span>
              {answered && option.correct && <Check className="w-5 h-5 text-green-400" />}
              {answered && option.selected && !option.correct && <X className="w-5 h-5 text-red-400" />}
            </div>
          </button>
        ))}
      </div>
      
      {answered && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-3 rounded-lg ${correct ? 'bg-green-500/20 border border-green-500/50' : 'bg-amber-500/20 border border-amber-500/50'}`}
        >
          <p className={`text-sm ${correct ? 'text-green-300' : 'text-amber-300'}`}>
            {correct ? '✓ Correct! ' : '✗ Incorrect. '}
            {explanation}
          </p>
        </motion.div>
      )}
    </div>
  );
};

// Hand Ranking Quiz
const HandRankingQuiz = ({ onComplete }) => {
  const [question, setQuestion] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correct, setCorrect] = useState(false);
  
  const generateQuestion = useCallback(() => {
    // Generate two hands with different strengths
    const strength1 = Math.floor(Math.random() * 7); // 0-6
    const strength2 = Math.min(9, strength1 + 1 + Math.floor(Math.random() * 3)); // higher
    
    const hand1 = generateHandWithStrength(strength1, strength1 + 1);
    const hand2 = generateHandWithStrength(strength2, strength2 + 1);
    
    if (!hand1 || !hand2) {
      generateQuestion();
      return;
    }
    
    const correctAnswer = hand2.strength > hand1.strength ? 'B' : 'A';
    
    setQuestion({
      hand1: { ...hand1, label: 'A' },
      hand2: { ...hand2, label: 'B' },
      correctAnswer,
      explanation: `${hand2.hand.hand.name} (Hand B) beats ${hand1.hand.hand.name} (Hand A) in hand rankings.`
    });
    setAnswered(false);
    setCorrect(false);
  }, []);
  
  useEffect(() => {
    generateQuestion();
  }, [generateQuestion]);
  
  const handleAnswer = (answer) => {
    const isCorrect = answer === question.correctAnswer;
    setCorrect(isCorrect);
    setAnswered(true);
    setTimeout(() => onComplete(isCorrect), 1500);
  };
  
  if (!question) return <div className="text-white">Loading...</div>;
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-white text-center">Which hand is stronger?</h3>
      
      <div className="grid grid-cols-2 gap-6">
        {/* Hand A */}
        <button
          onClick={() => !answered && handleAnswer('A')}
          disabled={answered}
          className={`p-4 rounded-xl border-2 transition-all ${
            answered && question.correctAnswer === 'A'
              ? 'border-green-500 bg-green-500/20'
              : answered && question.correctAnswer !== 'A'
                ? 'border-slate-600 bg-slate-800/50 opacity-50'
                : 'border-slate-600 bg-slate-800/50 hover:border-amber-500'
          }`}
        >
          <div className="text-sm font-bold text-amber-400 mb-2">Hand A</div>
          <div className="flex gap-1 justify-center mb-2">
            {question.hand1.holeCards.map((card, i) => (
              <MiniCard key={i} card={card} />
            ))}
          </div>
          <div className="flex gap-0.5 justify-center mb-2">
            {question.hand1.community.map((card, i) => (
              <MiniCard key={i} card={card} />
            ))}
          </div>
          {answered && (
            <div className="text-xs text-slate-300 mt-2">{question.hand1.hand.hand.name}</div>
          )}
        </button>
        
        {/* Hand B */}
        <button
          onClick={() => !answered && handleAnswer('B')}
          disabled={answered}
          className={`p-4 rounded-xl border-2 transition-all ${
            answered && question.correctAnswer === 'B'
              ? 'border-green-500 bg-green-500/20'
              : answered && question.correctAnswer !== 'B'
                ? 'border-slate-600 bg-slate-800/50 opacity-50'
                : 'border-slate-600 bg-slate-800/50 hover:border-amber-500'
          }`}
        >
          <div className="text-sm font-bold text-amber-400 mb-2">Hand B</div>
          <div className="flex gap-1 justify-center mb-2">
            {question.hand2.holeCards.map((card, i) => (
              <MiniCard key={i} card={card} />
            ))}
          </div>
          <div className="flex gap-0.5 justify-center mb-2">
            {question.hand2.community.map((card, i) => (
              <MiniCard key={i} card={card} />
            ))}
          </div>
          {answered && (
            <div className="text-xs text-slate-300 mt-2">{question.hand2.hand.hand.name}</div>
          )}
        </button>
      </div>
      
      {answered && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 rounded-lg text-center ${correct ? 'bg-green-500/20' : 'bg-amber-500/20'}`}
        >
          <p className={`text-sm ${correct ? 'text-green-300' : 'text-amber-300'}`}>
            {correct ? '✓ Correct! ' : '✗ Incorrect. '}
            {question.explanation}
          </p>
        </motion.div>
      )}
    </div>
  );
};

// Starting Hand Quiz
const StartingHandQuiz = ({ onComplete }) => {
  const [question, setQuestion] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [selected, setSelected] = useState(null);
  
  const generateQuestion = useCallback(() => {
    const categories = Object.keys(STARTING_HANDS);
    const category = categories[Math.floor(Math.random() * categories.length)];
    const hands = STARTING_HANDS[category];
    const hand = hands[Math.floor(Math.random() * hands.length)];
    
    setQuestion({
      cards: hand.cards,
      suited: hand.suited,
      correctAction: hand.action,
      reason: hand.reason
    });
    setAnswered(false);
    setCorrect(false);
    setSelected(null);
  }, []);
  
  useEffect(() => {
    generateQuestion();
  }, [generateQuestion]);
  
  const handleAnswer = (action) => {
    setSelected(action);
    const isCorrect = action === question.correctAction;
    setCorrect(isCorrect);
    setAnswered(true);
    setTimeout(() => onComplete(isCorrect), 1500);
  };
  
  if (!question) return null;
  
  // Create visual cards
  const suits = question.suited ? ['hearts', 'hearts'] : ['hearts', 'spades'];
  const displayCards = question.cards.map((c, i) => ({
    display: c,
    suit: suits[i],
    rank: c === 'A' ? 14 : c === 'K' ? 13 : c === 'Q' ? 12 : c === 'J' ? 11 : parseInt(c)
  }));
  
  const actions = [
    { value: 'raise', label: 'Raise', color: 'bg-green-600 hover:bg-green-500' },
    { value: 'call', label: 'Call', color: 'bg-blue-600 hover:bg-blue-500' },
    { value: 'fold', label: 'Fold', color: 'bg-red-600 hover:bg-red-500' }
  ];
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-white text-center">
        What should you do with this hand pre-flop?
      </h3>
      
      <div className="flex justify-center gap-2 mb-4">
        {displayCards.map((card, i) => (
          <MiniCard key={i} card={card} />
        ))}
        {question.suited && (
          <span className="text-xs text-amber-400 self-end ml-2">(Suited)</span>
        )}
      </div>
      
      <div className="flex justify-center gap-3">
        {actions.map((action) => (
          <button
            key={action.value}
            onClick={() => !answered && handleAnswer(action.value)}
            disabled={answered}
            className={`px-6 py-3 rounded-lg font-bold text-white transition-all ${
              answered
                ? action.value === question.correctAction
                  ? 'bg-green-500 ring-2 ring-green-300'
                  : action.value === selected
                    ? 'bg-red-500 ring-2 ring-red-300'
                    : 'bg-slate-700 opacity-50'
                : action.color
            }`}
          >
            {action.label}
          </button>
        ))}
      </div>
      
      {answered && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 rounded-lg text-center ${correct ? 'bg-green-500/20' : 'bg-amber-500/20'}`}
        >
          <p className={`text-sm ${correct ? 'text-green-300' : 'text-amber-300'}`}>
            {correct ? '✓ Correct! ' : '✗ Incorrect. '}
            {question.reason}
          </p>
        </motion.div>
      )}
    </div>
  );
};

// Pot Odds Quiz
const PotOddsQuiz = ({ onComplete }) => {
  const [question, setQuestion] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [selected, setSelected] = useState(null);
  
  const generateQuestion = useCallback(() => {
    const pot = Math.floor(Math.random() * 200) + 50;
    const toCall = Math.floor(Math.random() * 50) + 10;
    const outs = Math.floor(Math.random() * 12) + 2;
    
    // Pot odds: pot / toCall
    const potOdds = pot / toCall;
    
    // Equity with outs (rule of 4 for turn+river, rule of 2 for river only)
    const equity = outs * 4; // Assuming turn with 2 cards to come
    const breakEvenOdds = 100 / equity;
    
    // Should call if pot odds > break-even odds
    const shouldCall = potOdds >= breakEvenOdds * 0.9; // Give some margin
    
    setQuestion({
      pot,
      toCall,
      outs,
      potOdds: potOdds.toFixed(1),
      equity,
      shouldCall,
      explanation: shouldCall
        ? `With ${outs} outs (~${equity}% equity) and pot odds of ${potOdds.toFixed(1)}:1, calling is profitable.`
        : `With only ${outs} outs (~${equity}% equity) and pot odds of ${potOdds.toFixed(1)}:1, folding is better.`
    });
    setAnswered(false);
    setCorrect(false);
    setSelected(null);
  }, []);
  
  useEffect(() => {
    generateQuestion();
  }, [generateQuestion]);
  
  const handleAnswer = (answer) => {
    setSelected(answer);
    const isCorrect = answer === question.shouldCall;
    setCorrect(isCorrect);
    setAnswered(true);
    setTimeout(() => onComplete(isCorrect), 2000);
  };
  
  if (!question) return null;
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-white text-center">Should you call?</h3>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-slate-800/50 rounded-lg p-4 text-center">
          <div className="text-xs text-slate-500 mb-1">Pot Size</div>
          <div className="text-2xl font-bold text-amber-400">${question.pot}</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-4 text-center">
          <div className="text-xs text-slate-500 mb-1">To Call</div>
          <div className="text-2xl font-bold text-red-400">${question.toCall}</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-4 text-center">
          <div className="text-xs text-slate-500 mb-1">Your Outs</div>
          <div className="text-2xl font-bold text-cyan-400">{question.outs}</div>
        </div>
      </div>
      
      <div className="text-center text-sm text-slate-400 mb-4">
        Pot odds: {question.potOdds}:1 | Your equity: ~{question.equity}%
      </div>
      
      <div className="flex justify-center gap-4">
        <button
          onClick={() => !answered && handleAnswer(true)}
          disabled={answered}
          className={`px-8 py-3 rounded-lg font-bold text-white transition-all ${
            answered
              ? question.shouldCall
                ? 'bg-green-500 ring-2 ring-green-300'
                : selected === true
                  ? 'bg-red-500 ring-2 ring-red-300'
                  : 'bg-slate-700 opacity-50'
              : 'bg-green-600 hover:bg-green-500'
          }`}
        >
          Call
        </button>
        <button
          onClick={() => !answered && handleAnswer(false)}
          disabled={answered}
          className={`px-8 py-3 rounded-lg font-bold text-white transition-all ${
            answered
              ? !question.shouldCall
                ? 'bg-green-500 ring-2 ring-green-300'
                : selected === false
                  ? 'bg-red-500 ring-2 ring-red-300'
                  : 'bg-slate-700 opacity-50'
              : 'bg-red-600 hover:bg-red-500'
          }`}
        >
          Fold
        </button>
      </div>
      
      {answered && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 rounded-lg text-center ${correct ? 'bg-green-500/20' : 'bg-amber-500/20'}`}
        >
          <p className={`text-sm ${correct ? 'text-green-300' : 'text-amber-300'}`}>
            {correct ? '✓ Correct! ' : '✗ Incorrect. '}
            {question.explanation}
          </p>
        </motion.div>
      )}
    </div>
  );
};

// Main Practice Mode Component
const PokerPractice = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [questionNum, setQuestionNum] = useState(0);
  const maxQuestions = 10;
  
  const handleQuizComplete = (isCorrect) => {
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
    setQuestionNum(prev => prev + 1);
  };
  
  const resetQuiz = () => {
    setScore({ correct: 0, total: 0 });
    setQuestionNum(0);
  };
  
  const quizModes = [
    { id: QUIZ_TYPES.HAND_RANKING, icon: Trophy, title: 'Hand Rankings', desc: 'Compare two hands - which wins?', color: 'amber' },
    { id: QUIZ_TYPES.STARTING_HAND, icon: Target, title: 'Starting Hands', desc: 'Fold, call, or raise pre-flop?', color: 'green' },
    { id: QUIZ_TYPES.POT_ODDS, icon: Brain, title: 'Pot Odds', desc: 'Is the call profitable?', color: 'cyan' },
  ];
  
  // Quiz completed
  if (mode && questionNum >= maxQuestions) {
    const percentage = Math.round((score.correct / score.total) * 100);
    const grade = percentage >= 90 ? 'A+' : percentage >= 80 ? 'A' : percentage >= 70 ? 'B' : percentage >= 60 ? 'C' : 'D';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950/20 to-slate-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-slate-900/90 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center"
        >
          <div className="text-6xl mb-4">
            {percentage >= 80 ? '🏆' : percentage >= 60 ? '👍' : '📚'}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Quiz Complete!</h2>
          <div className="text-5xl font-bold text-amber-400 mb-2">{grade}</div>
          <p className="text-slate-400 mb-6">
            You got {score.correct} out of {score.total} correct ({percentage}%)
          </p>
          
          <div className="flex gap-3 justify-center">
            <Button onClick={resetQuiz} className="bg-amber-600 hover:bg-amber-500">
              <RefreshCw className="w-4 h-4 mr-2" /> Try Again
            </Button>
            <Button onClick={() => setMode(null)} variant="outline" className="border-slate-600">
              Other Quizzes
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // Mode selection
  if (!mode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950/20 to-slate-950">
        <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/games/poker')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-lg font-bold text-white">Practice Mode</h1>
          </div>
        </div>
        
        <div className="max-w-2xl mx-auto p-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Poker Practice</h2>
            <p className="text-slate-400">Test your poker knowledge with these quizzes</p>
          </div>
          
          <div className="space-y-4">
            {quizModes.map((quiz) => (
              <motion.button
                key={quiz.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setMode(quiz.id)}
                className={`w-full bg-slate-900/80 rounded-xl border border-slate-700 p-5 text-left hover:border-${quiz.color}-500/50 transition-all group`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-${quiz.color}-600/20 flex items-center justify-center`}>
                    <quiz.icon className={`w-7 h-7 text-${quiz.color}-400`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">{quiz.title}</h3>
                    <p className="text-sm text-slate-400">{quiz.desc}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Quiz in progress
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950/20 to-slate-950">
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => setMode(null)} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-lg font-bold text-white">
              {quizModes.find(q => q.id === mode)?.title}
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-400">
              Question {questionNum + 1}/{maxQuestions}
            </div>
            <div className="bg-slate-800 px-3 py-1 rounded-lg">
              <span className="text-green-400 font-bold">{score.correct}</span>
              <span className="text-slate-500">/</span>
              <span className="text-white">{score.total}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-2xl mx-auto p-6">
        {/* Progress bar */}
        <div className="w-full bg-slate-800 rounded-full h-2 mb-8">
          <div 
            className="bg-amber-500 h-2 rounded-full transition-all"
            style={{ width: `${(questionNum / maxQuestions) * 100}%` }}
          />
        </div>
        
        {mode === QUIZ_TYPES.HAND_RANKING && (
          <HandRankingQuiz key={questionNum} onComplete={handleQuizComplete} />
        )}
        {mode === QUIZ_TYPES.STARTING_HAND && (
          <StartingHandQuiz key={questionNum} onComplete={handleQuizComplete} />
        )}
        {mode === QUIZ_TYPES.POT_ODDS && (
          <PotOddsQuiz key={questionNum} onComplete={handleQuizComplete} />
        )}
      </div>
    </div>
  );
};

export default PokerPractice;
